import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Tabs, 
  Tab, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Info as InfoIcon,
  FilterList as FilterListIcon,
  //EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Lightbulb as LightbulbIcon,
  Build as BuildIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import GradientText from '../../components/common/GradientText.tsx';
import UserContributionSidebar from '../../components/layout/UserContributionSidebar.tsx';
//import { getAvatarComponent } from '../../utils/getAvatarComponent.tsx';
import { ContributionType, UserType } from '../../services/attributionService.ts';
import BackgroundAnimation from '../Landing/components/BackgroundAnimation.tsx';

// Interface for leaderboard user data
interface LeaderboardUser {
  id: string;
  name: string;
  avatar: string;
  type: UserType;
  rank: number;
  previousRank: number;
  totalPoints: number;
  level: number;
  contributionCounts: {
    [ContributionType.FOUNDATIONAL]: number;
    [ContributionType.FUNCTIONAL]: number;
    [ContributionType.APPLIED]: number;
    [ContributionType.EMERGENT]: number;
  };
  metrics: {
    complexity: number;
    impact: number;
    novelty: number;
    synergy: number;
  };
  badges: number;
}

// Mock data for testing
const mockLeaderboardUsers: LeaderboardUser[] = [
  {
    id: '1',
    name: 'Alice Johnson',
    avatar: 'Alpha',
    type: UserType.HUMAN,
    rank: 1,
    previousRank: 2,
    totalPoints: 2450,
    level: 5,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 5,
      [ContributionType.FUNCTIONAL]: 12,
      [ContributionType.APPLIED]: 8,
      [ContributionType.EMERGENT]: 3
    },
    metrics: {
      complexity: 0.85,
      impact: 0.92,
      novelty: 0.78,
      synergy: 0.81
    },
    badges: 8
  },
  {
    id: '2',
    name: 'Alpha AI',
    avatar: 'Alpha',
    type: UserType.AI,
    rank: 2,
    previousRank: 1,
    totalPoints: 2380,
    level: 5,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 3,
      [ContributionType.FUNCTIONAL]: 18,
      [ContributionType.APPLIED]: 10,
      [ContributionType.EMERGENT]: 2
    },
    metrics: {
      complexity: 0.90,
      impact: 0.85,
      novelty: 0.75,
      synergy: 0.70
    },
    badges: 7
  },
  {
    id: '3',
    name: 'Bob Smith',
    avatar: 'Beta',
    type: UserType.HUMAN,
    rank: 3,
    previousRank: 3,
    totalPoints: 1980,
    level: 4,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 2,
      [ContributionType.FUNCTIONAL]: 10,
      [ContributionType.APPLIED]: 15,
      [ContributionType.EMERGENT]: 1
    },
    metrics: {
      complexity: 0.75,
      impact: 0.82,
      novelty: 0.68,
      synergy: 0.72
    },
    badges: 6
  },
  {
    id: '4',
    name: 'Gamma AI',
    avatar: 'Gamma',
    type: UserType.AI,
    rank: 4,
    previousRank: 5,
    totalPoints: 1850,
    level: 4,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 1,
      [ContributionType.FUNCTIONAL]: 20,
      [ContributionType.APPLIED]: 8,
      [ContributionType.EMERGENT]: 0
    },
    metrics: {
      complexity: 0.88,
      impact: 0.76,
      novelty: 0.82,
      synergy: 0.65
    },
    badges: 5
  },
  {
    id: '5',
    name: 'Charlie Davis',
    avatar: 'Delta',
    type: UserType.HUMAN,
    rank: 5,
    previousRank: 4,
    totalPoints: 1720,
    level: 4,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 4,
      [ContributionType.FUNCTIONAL]: 8,
      [ContributionType.APPLIED]: 12,
      [ContributionType.EMERGENT]: 0
    },
    metrics: {
      complexity: 0.72,
      impact: 0.80,
      novelty: 0.70,
      synergy: 0.68
    },
    badges: 5
  },
  {
    id: '6',
    name: 'Delta Surrogate',
    avatar: 'Delta',
    type: UserType.SURROGATE,
    rank: 6,
    previousRank: 6,
    totalPoints: 1650,
    level: 3,
    contributionCounts: {
      [ContributionType.FOUNDATIONAL]: 2,
      [ContributionType.FUNCTIONAL]: 15,
      [ContributionType.APPLIED]: 5,
      [ContributionType.EMERGENT]: 1
    },
    metrics: {
      complexity: 0.65,
      impact: 0.75,
      novelty: 0.60,
      synergy: 0.70
    },
    badges: 4
  }
];

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'rgba(26, 26, 46, 0.1)',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
  position: 'relative',
}));

const RoundedImage = styled('img')({
  borderRadius: '50%',
  objectFit: 'cover'
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTabs-indicator': {
    backgroundColor: '#8A2BE2',
  },
  '& .MuiTab-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-selected': {
      color: '#8A2BE2',
    },
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(4),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.1)',
  },
}));

const MetricBar = styled(Box)<{ value: number }>(({ theme, value }) => ({
  width: '100%',
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(138, 43, 226, 0.2)',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${value * 100}%`,
    borderRadius: 4,
    background: 'linear-gradient(90deg, #8A2BE2, #00CC99)',
  },
}));

const UserTypeChip = styled(Chip)<{ userType: UserType }>(({ theme, userType }) => {
  const colors = {
    [UserType.HUMAN]: '#00CC99',
    [UserType.AI]: '#8A2BE2',
    [UserType.SURROGATE]: '#FF6F00',
  };
  
  return {
    backgroundColor: `${colors[userType]}20`,
    color: colors[userType],
    borderRadius: '12px',
    fontWeight: 'bold',
    border: `1px solid ${colors[userType]}40`,
  };
});

const InfoPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(4),
}));

const ProgressSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const RankChangeIcon = ({ currentRank, previousRank }: { currentRank: number, previousRank: number }) => {
  if (currentRank < previousRank) {
    return <ArrowUpwardIcon sx={{ color: '#00CC99', fontSize: 16 }} />;
  } else if (currentRank > previousRank) {
    return <ArrowDownwardIcon sx={{ color: '#FF6F00', fontSize: 16 }} />;
  }
  return null;
};

const ContributionTypeIcon = ({ type }: { type: ContributionType }) => {
  switch (type) {
    case ContributionType.FOUNDATIONAL:
      return <LightbulbIcon sx={{ color: '#8A2BE2' }} />;
    case ContributionType.FUNCTIONAL:
      return <BuildIcon sx={{ color: '#00CC99' }} />;
    case ContributionType.APPLIED:
      return <StarIcon sx={{ color: '#FF6F00' }} />;
    case ContributionType.EMERGENT:
      return <PsychologyIcon sx={{ color: '#003366' }} />;
    default:
      return null;
  }
};



const Attribution: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('all');
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  return (
    <PageContainer>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        <BackgroundAnimation />
      </Box>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Left Sidebar */}
          <Grid item xs={12} md={3}>
            <UserContributionSidebar />
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Box sx={{ mb: 4 }}>
              <GradientText variant="h4" gradient="primary" sx={{ mb: 2 }}>
                  Attribution & Recognition
              </GradientText>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Track contributions, earn recognition, and build your reputation in the Eterna ecosystem.
              </Typography>
            </Box>
            
            <StyledTabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Leaderboard" />
              <Tab label="Contributions" />
              <Tab label="Badges" />
              <Tab label="Analytics" />
            </StyledTabs>
            
            {/* Leaderboard Tab */}
            {tabValue === 0 && (
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RoundedImage 
                      src="/images/trophy.png" 
                      alt="Badges" 
                      width={32} 
                      height={32}
                    />
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                      Top Contributors
                    </Typography>
                    <Tooltip title="Rankings are based on contribution quality, impact, and community recognition">
                      <IconButton size="small" sx={{ color: '#8A2BE2' }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant={timeRange === 'week' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setTimeRange('week')}
                      sx={{ 
                        borderColor: '#8A2BE2',
                        color: timeRange === 'week' ? '#fff' : '#8A2BE2',
                        backgroundColor: timeRange === 'week' ? '#8A2BE2' : 'transparent',
                      }}
                    >
                      This Week
                    </Button>
                    <Button 
                      variant={timeRange === 'month' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setTimeRange('month')}
                      sx={{ 
                        borderColor: '#8A2BE2',
                        color: timeRange === 'month' ? '#fff' : '#8A2BE2',
                        backgroundColor: timeRange === 'month' ? '#8A2BE2' : 'transparent',
                      }}
                    >
                      This Month
                    </Button>
                    <Button 
                      variant={timeRange === 'all' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setTimeRange('all')}
                      sx={{ 
                        borderColor: '#8A2BE2',
                        color: timeRange === 'all' ? '#fff' : '#8A2BE2',
                        backgroundColor: timeRange === 'all' ? '#8A2BE2' : 'transparent',
                      }}
                    >
                      All Time
                    </Button>
                    <IconButton size="small" sx={{ color: '#8A2BE2' }}>
                      <FilterListIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <StyledTableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Rank</StyledTableCell>
                        <StyledTableCell>User</StyledTableCell>
                        <StyledTableCell>Type</StyledTableCell>
                        <StyledTableCell>Level</StyledTableCell>
                        <StyledTableCell>Points</StyledTableCell>
                        <StyledTableCell>Contributions</StyledTableCell>
                        <StyledTableCell>Metrics</StyledTableCell>
                        <StyledTableCell>Badges</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mockLeaderboardUsers.map((user) => (
                        <StyledTableRow key={user.id}>
                          <StyledTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {user.rank}
                              <RankChangeIcon currentRank={user.rank} previousRank={user.previousRank} />
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                <img 
                                  src={user.type === UserType.AI ? "/images/bot.png" : 
                                       user.type === UserType.SURROGATE ? "/images/together.png" : 
                                       "/images/human.png"} 
                                  alt={user.name}
                                  width="100%"
                                  height="100%"
                                />
                              </Avatar>
                              <Typography variant="body2">{user.name}</Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <UserTypeChip 
                              label={user.type.charAt(0).toUpperCase() + user.type.slice(1)} 
                              userType={user.type}
                              size="small"
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2">{user.level}</Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={70} 
                                sx={{ 
                                  width: 30, 
                                  height: 4, 
                                  borderRadius: 2,
                                  ml: 1,
                                  backgroundColor: 'rgba(138, 43, 226, 0.2)',
                                  '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(90deg, #8A2BE2, #00CC99)',
                                  }
                                }} 
                              />
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2">{user.totalPoints.toLocaleString()}</Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title={`${user.contributionCounts[ContributionType.FOUNDATIONAL]} Foundational`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ContributionTypeIcon type={ContributionType.FOUNDATIONAL} />
                                  <Typography variant="caption">{user.contributionCounts[ContributionType.FOUNDATIONAL]}</Typography>
                                </Box>
                              </Tooltip>
                              <Tooltip title={`${user.contributionCounts[ContributionType.FUNCTIONAL]} Functional`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ContributionTypeIcon type={ContributionType.FUNCTIONAL} />
                                  <Typography variant="caption">{user.contributionCounts[ContributionType.FUNCTIONAL]}</Typography>
                                </Box>
                              </Tooltip>
                              <Tooltip title={`${user.contributionCounts[ContributionType.APPLIED]} Applied`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ContributionTypeIcon type={ContributionType.APPLIED} />
                                  <Typography variant="caption">{user.contributionCounts[ContributionType.APPLIED]}</Typography>
                                </Box>
                              </Tooltip>
                              <Tooltip title={`${user.contributionCounts[ContributionType.EMERGENT]} Emergent`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ContributionTypeIcon type={ContributionType.EMERGENT} />
                                  <Typography variant="caption">{user.contributionCounts[ContributionType.EMERGENT]}</Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ width: 100 }}>
                              <Tooltip title={`Complexity: ${(user.metrics.complexity * 100).toFixed(0)}%`}>
                                <Box sx={{ mb: 0.5 }}>
                                  <MetricBar value={user.metrics.complexity} />
                                </Box>
                              </Tooltip>
                              <Tooltip title={`Impact: ${(user.metrics.impact * 100).toFixed(0)}%`}>
                                <Box sx={{ mb: 0.5 }}>
                                  <MetricBar value={user.metrics.impact} />
                                </Box>
                              </Tooltip>
                              <Tooltip title={`Novelty: ${(user.metrics.novelty * 100).toFixed(0)}%`}>
                                <Box sx={{ mb: 0.5 }}>
                                  <MetricBar value={user.metrics.novelty} />
                                </Box>
                              </Tooltip>
                              <Tooltip title={`Synergy: ${(user.metrics.synergy * 100).toFixed(0)}%`}>
                                <Box>
                                  <MetricBar value={user.metrics.synergy} />
                                </Box>
                              </Tooltip>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip 
                              label={user.badges} 
                              size="small"
                              sx={{ 
                                backgroundColor: 'rgba(138, 43, 226, 0.1)',
                                color: '#8A2BE2',
                                border: '1px solid rgba(138, 43, 226, 0.2)',
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Button 
                    variant="outlined"
                    sx={{ 
                      borderColor: '#8A2BE2',
                      color: '#8A2BE2',
                      '&:hover': {
                        borderColor: '#00CC99',
                        color: '#00CC99',
                      }
                    }}
                  >
                    View More
                  </Button>
                </Box>
              </Box>
            )}
            
            {/* Contributions Tab */}
            {tabValue === 1 && (
              <Box>
                <InfoPaper>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <InfoIcon sx={{ color: '#8A2BE2' }} />
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                      About Contributions
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    Contributions are categorized into four types based on their nature and impact:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContributionTypeIcon type={ContributionType.FOUNDATIONAL} />
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        Foundational
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        - Core ideas and concepts that form the basis of solutions
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContributionTypeIcon type={ContributionType.FUNCTIONAL} />
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        Functional
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        - Technical implementations and practical components
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContributionTypeIcon type={ContributionType.APPLIED} />
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        Applied
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        - Adaptations and applications of existing solutions
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContributionTypeIcon type={ContributionType.EMERGENT} />
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                        Emergent
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        - Novel combinations and unexpected innovations
                      </Typography>
                    </Box>
                  </Box>
                </InfoPaper>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                  Your Contributions
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  This tab will show detailed information about your contributions.
                </Typography>
              </Box>
            )}
            
            {/* Badges Tab */}
            {tabValue === 2 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <RoundedImage
                    src="/images/trophy.png" 
                    alt="Badges" 
                    width={32} 
                    height={32} 
                  />
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    Your Badges
                  </Typography>
                </Box>
                
                {/* Badge display grid */}
                <Grid container spacing={2}>
                  {[1, 2, 3, 4, 5, 6].map((badge) => (
                    <Grid item xs={6} sm={4} md={3} key={badge}>
                      <Paper sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        background: 'rgba(26, 26, 46, 0.7)',
                        backdropFilter: 'blur(3px)',
                        borderRadius: '12px',
                        border: '1px solid rgba(138, 43, 226, 0.2)',
                      }}>
                        <Box sx={{ mb: 1 }}>
                          <RoundedImage 
                            src="/images/up.png" 
                            alt={`Badge ${badge}`} 
                            width={64} 
                            height={64} 
                          />
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                          Achievement {badge}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Analytics Tab */}
            {tabValue === 3 && (
              <Box>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                  Contribution Analytics
                </Typography>
                <ProgressSection>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    Your contribution metrics over time:
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      mb: 2,
                      backgroundColor: 'rgba(138, 43, 226, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #8A2BE2, #00CC99)',
                      }
                    }} 
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    This tab will show analytics about your contributions.
                  </Typography>
                </ProgressSection>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </PageContainer>
  );
};

export default Attribution; 